import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const Step4InfoClinica = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 mb-4">
        Complete solo las secciones relevantes para su solicitud. Haga clic en cada sección para expandirla.
      </p>

      <Accordion type="multiple" className="w-full">
        {/* Datos Generales */}
        <AccordionItem value="datos-generales" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="font-semibold text-[#505BB3]">Datos Generales</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>¿Toma antibióticos actualmente?</Label>
                <RadioGroup 
                  value={formData.tomaAntibioticos} 
                  onValueChange={(value) => updateFormData('tomaAntibioticos', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Si" id="antiSi" />
                    <Label htmlFor="antiSi" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="antiNo" />
                    <Label htmlFor="antiNo" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.tomaAntibioticos === 'Si' && (
                <div className="space-y-2 ml-4 animate-in slide-in-from-top-2">
                  <Label htmlFor="fechaUltimaDosis">Fecha de última dosis:</Label>
                  <Input
                    id="fechaUltimaDosis"
                    type="date"
                    value={formData.fechaUltimaDosis}
                    onChange={(e) => updateFormData('fechaUltimaDosis', e.target.value)}
                    className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Alérgico a antibióticos:</Label>
                <RadioGroup 
                  value={formData.alergicoAntibioticos} 
                  onValueChange={(value) => updateFormData('alergicoAntibioticos', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Si" id="alergSi" />
                    <Label htmlFor="alergSi" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="alergNo" />
                    <Label htmlFor="alergNo" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.alergicoAntibioticos === 'Si' && (
                <div className="space-y-2 ml-4 animate-in slide-in-from-top-2">
                  <Label htmlFor="cualAlergia">¿Cuál?</Label>
                  <Input
                    id="cualAlergia"
                    type="text"
                    placeholder="Especifique el antibiótico"
                    value={formData.cualAlergia}
                    onChange={(e) => updateFormData('cualAlergia', e.target.value)}
                    className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="tiempoEvolucion">Tiempo de Evolución:</Label>
                <Input
                  id="tiempoEvolucion"
                  type="text"
                  placeholder="Ej: 3 días, 2 semanas"
                  value={formData.tiempoEvolucion}
                  onChange={(e) => updateFormData('tiempoEvolucion', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="origenMuestra">Origen de la toma de muestra:</Label>
                <Input
                  id="origenMuestra"
                  type="text"
                  placeholder="Especifique el origen de la muestra"
                  value={formData.origenMuestra}
                  onChange={(e) => updateFormData('origenMuestra', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Recolección de Sangre */}
        <AccordionItem value="sangre" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="font-semibold text-[#505BB3]">Recolección de Sangre</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sangreJeringa"
                  checked={formData.sangreJeringa}
                  onCheckedChange={(checked) => updateFormData('sangreJeringa', checked)}
                />
                <Label htmlFor="sangreJeringa" className="cursor-pointer">Jeringa</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sangreSistemaVacio"
                  checked={formData.sangreSistemaVacio}
                  onCheckedChange={(checked) => updateFormData('sangreSistemaVacio', checked)}
                />
                <Label htmlFor="sangreSistemaVacio" className="cursor-pointer">Sistema de vacío</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sangreCateter"
                  checked={formData.sangreCateter}
                  onCheckedChange={(checked) => updateFormData('sangreCateter', checked)}
                />
                <Label htmlFor="sangreCateter" className="cursor-pointer">Muestra tomada de catéter</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sangreOtra">Otra:</Label>
                <Input
                  id="sangreOtra"
                  type="text"
                  placeholder="Especifique"
                  value={formData.sangreOtra}
                  onChange={(e) => updateFormData('sangreOtra', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Recolección de Orina */}
        <AccordionItem value="orina" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
              <span className="font-semibold text-[#505BB3]">Recolección de Orina</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="orinaCistocentesisTraumatica"
                  checked={formData.orinaCistocentesisTraumatica}
                  onCheckedChange={(checked) => updateFormData('orinaCistocentesisTraumatica', checked)}
                />
                <Label htmlFor="orinaCistocentesisTraumatica" className="cursor-pointer">Cistocentesis traumática</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="orinaCistocentesisNoTraumatica"
                  checked={formData.orinaCistocentesisNoTraumatica}
                  onCheckedChange={(checked) => updateFormData('orinaCistocentesisNoTraumatica', checked)}
                />
                <Label htmlFor="orinaCistocentesisNoTraumatica" className="cursor-pointer">Cistocentesis no traumática</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="orinaSondaje"
                  checked={formData.orinaSondaje}
                  onCheckedChange={(checked) => updateFormData('orinaSondaje', checked)}
                />
                <Label htmlFor="orinaSondaje" className="cursor-pointer">Sondaje uretral</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="orinaMiccionEspontanea"
                  checked={formData.orinaMiccionEspontanea}
                  onCheckedChange={(checked) => updateFormData('orinaMiccionEspontanea', checked)}
                />
                <Label htmlFor="orinaMiccionEspontanea" className="cursor-pointer">Micción espontánea</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="orinaOtra">Otra:</Label>
                <Input
                  id="orinaOtra"
                  type="text"
                  placeholder="Especifique"
                  value={formData.orinaOtra}
                  onChange={(e) => updateFormData('orinaOtra', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Hisopados / Tejidos / Exudados */}
        <AccordionItem value="hisopados" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="font-semibold text-[#505BB3]">Hisopados / Tejidos / Exudados</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hisopadoStuart"
                  checked={formData.hisopadoStuart}
                  onCheckedChange={(checked) => updateFormData('hisopadoStuart', checked)}
                />
                <Label htmlFor="hisopadoStuart" className="cursor-pointer">Stuart</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hisopadoAmies"
                  checked={formData.hisopadoAmies}
                  onCheckedChange={(checked) => updateFormData('hisopadoAmies', checked)}
                />
                <Label htmlFor="hisopadoAmies" className="cursor-pointer">Amies</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hisopadoSolucionSalina"
                  checked={formData.hisopadoSolucionSalina}
                  onCheckedChange={(checked) => updateFormData('hisopadoSolucionSalina', checked)}
                />
                <Label htmlFor="hisopadoSolucionSalina" className="cursor-pointer">Solución salina fisiológica 0.9%</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hisopadoSecoEsteril"
                  checked={formData.hisopadoSecoEsteril}
                  onCheckedChange={(checked) => updateFormData('hisopadoSecoEsteril', checked)}
                />
                <Label htmlFor="hisopadoSecoEsteril" className="cursor-pointer">Seco estéril</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hisopadoRefrigerado"
                  checked={formData.hisopadoRefrigerado}
                  onCheckedChange={(checked) => updateFormData('hisopadoRefrigerado', checked)}
                />
                <Label htmlFor="hisopadoRefrigerado" className="cursor-pointer">Refrigerado</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hisopadoOtra">Otra:</Label>
                <Input
                  id="hisopadoOtra"
                  type="text"
                  placeholder="Especifique"
                  value={formData.hisopadoOtra}
                  onChange={(e) => updateFormData('hisopadoOtra', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="observacionesConservacion">Observaciones sobre la conservación de la muestra:</Label>
                <Textarea
                  id="observacionesConservacion"
                  placeholder="Detalles adicionales sobre la conservación"
                  value={formData.observacionesConservacion}
                  onChange={(e) => updateFormData('observacionesConservacion', e.target.value)}
                  rows={3}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3] resize-none"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Cultivo Faríngea */}
        <AccordionItem value="faringea" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-pink-500"></div>
              <span className="font-semibold text-[#505BB3]">Cultivo-Microscopía Faríngea/Nasofaríngea</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="faringeaUlceraciones"
                  checked={formData.faringeaUlceraciones}
                  onCheckedChange={(checked) => updateFormData('faringeaUlceraciones', checked)}
                />
                <Label htmlFor="faringeaUlceraciones" className="cursor-pointer">Ulceraciones</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="faringeaManchasBlanquecinas"
                  checked={formData.faringeaManchasBlanquecinas}
                  onCheckedChange={(checked) => updateFormData('faringeaManchasBlanquecinas', checked)}
                />
                <Label htmlFor="faringeaManchasBlanquecinas" className="cursor-pointer">Manchas blanquecinas</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="faringeaTos"
                  checked={formData.faringeaTos}
                  onCheckedChange={(checked) => updateFormData('faringeaTos', checked)}
                />
                <Label htmlFor="faringeaTos" className="cursor-pointer">Tos</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="faringeaAmigdalasInflamadas"
                  checked={formData.faringeaAmigdalasInflamadas}
                  onCheckedChange={(checked) => updateFormData('faringeaAmigdalasInflamadas', checked)}
                />
                <Label htmlFor="faringeaAmigdalasInflamadas" className="cursor-pointer">Amígdalas inflamadas</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="faringeaNormal"
                  checked={formData.faringeaNormal}
                  onCheckedChange={(checked) => updateFormData('faringeaNormal', checked)}
                />
                <Label htmlFor="faringeaNormal" className="cursor-pointer">Región amigdalina y faríngea normales</Label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Exudado Diversos */}
        <AccordionItem value="exudado" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="font-semibold text-[#505BB3]">Cultivo-Microscopía Exudado Diversos/Líquidos Corporales</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="exudadoPurulento"
                  checked={formData.exudadoPurulento}
                  onCheckedChange={(checked) => updateFormData('exudadoPurulento', checked)}
                />
                <Label htmlFor="exudadoPurulento" className="cursor-pointer">Exudado purulento</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="exudadoSanguinolento"
                  checked={formData.exudadoSanguinolento}
                  onCheckedChange={(checked) => updateFormData('exudadoSanguinolento', checked)}
                />
                <Label htmlFor="exudadoSanguinolento" className="cursor-pointer">Exudado sanguinolento</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="exudadoDolorManipulacion"
                  checked={formData.exudadoDolorManipulacion}
                  onCheckedChange={(checked) => updateFormData('exudadoDolorManipulacion', checked)}
                />
                <Label htmlFor="exudadoDolorManipulacion" className="cursor-pointer">Dolor a la manipulación</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="exudadoFiebre"
                  checked={formData.exudadoFiebre}
                  onCheckedChange={(checked) => updateFormData('exudadoFiebre', checked)}
                />
                <Label htmlFor="exudadoFiebre" className="cursor-pointer">Fiebre documentada (MVZ)</Label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Cultivo Nasal */}
        <AccordionItem value="nasal" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
              <span className="font-semibold text-[#505BB3]">Cultivo-Microscopía Nasal</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="nasalEstornudos"
                  checked={formData.nasalEstornudos}
                  onCheckedChange={(checked) => updateFormData('nasalEstornudos', checked)}
                />
                <Label htmlFor="nasalEstornudos" className="cursor-pointer">Estornudos</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="nasalSecrecion"
                  checked={formData.nasalSecrecion}
                  onCheckedChange={(checked) => updateFormData('nasalSecrecion', checked)}
                />
                <Label htmlFor="nasalSecrecion" className="cursor-pointer">Secreción o congestión nasal</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="nasalCuadroRecurrente"
                  checked={formData.nasalCuadroRecurrente}
                  onCheckedChange={(checked) => updateFormData('nasalCuadroRecurrente', checked)}
                />
                <Label htmlFor="nasalCuadroRecurrente" className="cursor-pointer">Cuadro respiratorio recurrente</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="nasalSinAlteraciones"
                  checked={formData.nasalSinAlteraciones}
                  onCheckedChange={(checked) => updateFormData('nasalSinAlteraciones', checked)}
                />
                <Label htmlFor="nasalSinAlteraciones" className="cursor-pointer">Fosas nasales sin alteraciones visibles</Label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Coprocultivo */}
        <AccordionItem value="coprocultivo" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-lime-500"></div>
              <span className="font-semibold text-[#505BB3]">Coprocultivo</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprDiarreaCronica"
                  checked={formData.coprDiarreaCronica}
                  onCheckedChange={(checked) => updateFormData('coprDiarreaCronica', checked)}
                />
                <Label htmlFor="coprDiarreaCronica" className="cursor-pointer">Diarrea crónica</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprDiarreaAguda"
                  checked={formData.coprDiarreaAguda}
                  onCheckedChange={(checked) => updateFormData('coprDiarreaAguda', checked)}
                />
                <Label htmlFor="coprDiarreaAguda" className="cursor-pointer">Diarrea aguda/reciente</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprDolorAbdominal"
                  checked={formData.coprDolorAbdominal}
                  onCheckedChange={(checked) => updateFormData('coprDolorAbdominal', checked)}
                />
                <Label htmlFor="coprDolorAbdominal" className="cursor-pointer">Signos compatibles con dolor abdominal</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprVomito"
                  checked={formData.coprVomito}
                  onCheckedChange={(checked) => updateFormData('coprVomito', checked)}
                />
                <Label htmlFor="coprVomito" className="cursor-pointer">Vómito</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprLetargia"
                  checked={formData.coprLetargia}
                  onCheckedChange={(checked) => updateFormData('coprLetargia', checked)}
                />
                <Label htmlFor="coprLetargia" className="cursor-pointer">Letargia/decaimiento</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="coprFiebre"
                  checked={formData.coprFiebre}
                  onCheckedChange={(checked) => updateFormData('coprFiebre', checked)}
                />
                <Label htmlFor="coprFiebre" className="cursor-pointer">Fiebre documentada (MVZ)</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coprIngestaSospechosa">Ingesta de alimento/líquido sospechoso:</Label>
                <Input
                  id="coprIngestaSospechosa"
                  type="text"
                  placeholder="Describa si aplica"
                  value={formData.coprIngestaSospechosa}
                  onChange={(e) => updateFormData('coprIngestaSospechosa', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
              {formData.coprIngestaSospechosa && (
                <div className="space-y-2 ml-4 animate-in slide-in-from-top-2">
                  <Label htmlFor="coprCualIngesta">¿Cuál?</Label>
                  <Input
                    id="coprCualIngesta"
                    type="text"
                    placeholder="Especifique el alimento o líquido"
                    value={formData.coprCualIngesta}
                    onChange={(e) => updateFormData('coprCualIngesta', e.target.value)}
                    className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Urocultivo */}
        <AccordionItem value="urocultivo" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
              <span className="font-semibold text-[#505BB3]">Urocultivo-Microscopía de Sedimento Urinario</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>¿Antecedente de infección urinaria diagnosticada por MVZ?</Label>
                <RadioGroup 
                  value={formData.uroAntecedenteInfeccion} 
                  onValueChange={(value) => updateFormData('uroAntecedenteInfeccion', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Si" id="uroAntSi" />
                    <Label htmlFor="uroAntSi" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="uroAntNo" />
                    <Label htmlFor="uroAntNo" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>¿Ha notado olor fuerte o fétido en la orina?</Label>
                <RadioGroup 
                  value={formData.uroOlorFuerte} 
                  onValueChange={(value) => updateFormData('uroOlorFuerte', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Si" id="uroOlorSi" />
                    <Label htmlFor="uroOlorSi" className="cursor-pointer">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="uroOlorNo" />
                    <Label htmlFor="uroOlorNo" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroDolorAbdominal"
                    checked={formData.uroDolorAbdominal}
                    onCheckedChange={(checked) => updateFormData('uroDolorAbdominal', checked)}
                  />
                  <Label htmlFor="uroDolorAbdominal" className="cursor-pointer">Signos compatibles con dolor abdominal o lumbar</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroDolorPalpacion"
                    checked={formData.uroDolorPalpacion}
                    onCheckedChange={(checked) => updateFormData('uroDolorPalpacion', checked)}
                  />
                  <Label htmlFor="uroDolorPalpacion" className="cursor-pointer">Dolor abdominal a la palpación (MVZ)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroEsfuerzoOrinar"
                    checked={formData.uroEsfuerzoOrinar}
                    onCheckedChange={(checked) => updateFormData('uroEsfuerzoOrinar', checked)}
                  />
                  <Label htmlFor="uroEsfuerzoOrinar" className="cursor-pointer">Esfuerzo o dolor aparente al orinar</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroMiccionesFrecuentes"
                    checked={formData.uroMiccionesFrecuentes}
                    onCheckedChange={(checked) => updateFormData('uroMiccionesFrecuentes', checked)}
                  />
                  <Label htmlFor="uroMiccionesFrecuentes" className="cursor-pointer">Micciones frecuentes con poco volumen</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroSecrecionUretral"
                    checked={formData.uroSecrecionUretral}
                    onCheckedChange={(checked) => updateFormData('uroSecrecionUretral', checked)}
                  />
                  <Label htmlFor="uroSecrecionUretral" className="cursor-pointer">Secreción uretral o vaginal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="uroOrinaTurbia"
                    checked={formData.uroOrinaTurbia}
                    onCheckedChange={(checked) => updateFormData('uroOrinaTurbia', checked)}
                  />
                  <Label htmlFor="uroOrinaTurbia" className="cursor-pointer">Orina turbia o sangre visible en orina</Label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Hongos */}
        <AccordionItem value="hongos" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              <span className="font-semibold text-[#505BB3]">Cultivo-Microscopía Búsqueda de Hongos</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de lesión observada:</Label>
                <RadioGroup 
                  value={formData.hongosTipoLesion} 
                  onValueChange={(value) => updateFormData('hongosTipoLesion', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Descamativa" id="hongosDesc" />
                    <Label htmlFor="hongosDesc" className="cursor-pointer">Descamativa</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Exudativa" id="hongosExud" />
                    <Label htmlFor="hongosExud" className="cursor-pointer">Exudativa</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hongosSitioLesion">Sitio de lesión:</Label>
                <Input
                  id="hongosSitioLesion"
                  type="text"
                  placeholder="Especifique el sitio de la lesión"
                  value={formData.hongosSitioLesion}
                  onChange={(e) => updateFormData('hongosSitioLesion', e.target.value)}
                  className="border-gray-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Traqueal/Bronquial */}
        <AccordionItem value="traqueal" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-teal-500"></div>
              <span className="font-semibold text-[#505BB3]">Cultivo-Microscopía Traqueal/Bronquial</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealTosCronica"
                  checked={formData.traquealTosCronica}
                  onCheckedChange={(checked) => updateFormData('traquealTosCronica', checked)}
                />
                <Label htmlFor="traquealTosCronica" className="cursor-pointer">Tos crónica</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealTosReciente"
                  checked={formData.traquealTosReciente}
                  onCheckedChange={(checked) => updateFormData('traquealTosReciente', checked)}
                />
                <Label htmlFor="traquealTosReciente" className="cursor-pointer">Tos reciente</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealDificultadRespiratoria"
                  checked={formData.traquealDificultadRespiratoria}
                  onCheckedChange={(checked) => updateFormData('traquealDificultadRespiratoria', checked)}
                />
                <Label htmlFor="traquealDificultadRespiratoria" className="cursor-pointer">Dificultad respiratoria (disnea)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealRespiracionRapida"
                  checked={formData.traquealRespiracionRapida}
                  onCheckedChange={(checked) => updateFormData('traquealRespiracionRapida', checked)}
                />
                <Label htmlFor="traquealRespiracionRapida" className="cursor-pointer">Respiración rápida (taquipnea)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealPerdidaPeso"
                  checked={formData.traquealPerdidaPeso}
                  onCheckedChange={(checked) => updateFormData('traquealPerdidaPeso', checked)}
                />
                <Label htmlFor="traquealPerdidaPeso" className="cursor-pointer">Pérdida de peso</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="traquealFiebre"
                  checked={formData.traquealFiebre}
                  onCheckedChange={(checked) => updateFormData('traquealFiebre', checked)}
                />
                <Label htmlFor="traquealFiebre" className="cursor-pointer">Fiebre documentada (MVZ)</Label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default Step4InfoClinica;
